import { ISignalP1 } from "../../signal/ISignalP1";
import { PointerDetector } from "../PointerDetector";
import { Pointer } from "../Pointer";
import { IGesture } from "./IGesture";
import { ISignalP2 } from "../../signal/ISignalP2";
import { PinchZoomGesture, IPinchZoomGestureData } from "./PinchZoomGesture";
import { Signal } from "../../signal/Signal";

export interface IGestureDetectorConfig
{
	pointerDetector?: PointerDetector;
	element?: HTMLElement;
}

/**
 * Manages a simple pan and a pinch zoom gesture in a way that each always
 * completes a full event cycle, eg.:
 * - pinch start, [update,] end, pan start, [update,] end
 * So pinch start is always followed by pinch end (or updates in between),
 * but never a pan start, they are not interwoven.
 */
export class PanAndZoomGestures
{
	private _pointerDetector: PointerDetector;

	private _pan: {
		start: ISignalP1<Pointer>;
		update: ISignalP1<Pointer>;
		end: ISignalP2<Pointer, IGesture>;
	};
	private _panPointer: Pointer;
	private _panStarted: boolean = false;

	private _pinchZoom: PinchZoomGesture;

	constructor(config: IGestureDetectorConfig)
	{
		this._pointerDetector = config.pointerDetector || new PointerDetector({
			element: config.element,
			maxPointers: 2
		});

		this.init();
	}

	private init()
	{
		this._pan = {
			start: new Signal(),
			update: new Signal(),
			end: new Signal()
		};

		this._pointerDetector.down.add(this.onPointerDown);
		this._pointerDetector.move.add(this.onPointerMove);
		this._pointerDetector.up.add(this.onPointerUp);

		this._pinchZoom = new PinchZoomGesture(this._pointerDetector);
		this._pinchZoom.startSignal.add(this.onStartPinchZoom);
		this._pinchZoom.endSignal.add(this.onEndPinchZoom);
		this._pinchZoom.listen();
	}

	// ==================================================================================================
	// Pointer events

	private onPointerDown = (pointer: Pointer) =>
	{
		// first pointer down -> start pan
		this.tryPanStart();
	}

	private onPointerMove = (pointer: Pointer) =>
	{
		// if 1 pointer down -> update
		if (this._pointerDetector.pointersLength === 1 && this._panStarted)
		{
			this._pan.update.dispatch(pointer);
		}
	}

	private onPointerUp = (pointer: Pointer) =>
	{
		// this runs whenever a pointer is released:
		// A. when panning and that single finger is released
		// B. before pinch zoom completes (before onEndPinchZoom)

		if (this._pointerDetector.pointersLength === 0 && this._panStarted)
		{
			this.finishPan();
		}
	}

	private tryPanStart()
	{
		if (this._pointerDetector.pointersLength === 1 && !this._panStarted)
		{
			this._panStarted = true;
			this._panPointer = this._pointerDetector.pointerArray[0];

			// We need to reset dx, dy, offsetX, offsetY, because this is
			// considered a new pan start gesture (dispatched here manually).
			// Otherwise these properties may have big values, if the user
			// moved his fingers during pinching, which would cause some jumpingsure

			this._panPointer.dx = 0;
			this._panPointer.dy = 0;
			this._panPointer.offsetX = 0;
			this._panPointer.offsetY = 0;
			this._panPointer.startX = this._panPointer.localX;
			this._panPointer.startY = this._panPointer.localY;

			this._pan.start.dispatch(this._panPointer);
		}
	}

	private finishPan(breakingGesture: IGesture = null)
	{
		this._panStarted = false;
		this._pan.end.dispatch(this._panPointer, breakingGesture);
		this._panPointer = null;
	}

	// ==================================================================================================
	// Pinch zoom events

	private onStartPinchZoom = (zoomData: IPinchZoomGestureData) =>
	{
		// stop pan if it's in progress

		if (this._panStarted)
		{
			this.finishPan(this._pinchZoom);
		}
	}

	private onEndPinchZoom = (zoomData: IPinchZoomGestureData) =>
	{
		// check if a pan needs to be started
		// (when switching from 2 fingers to 1)

		this.tryPanStart();
	}

	// ==================================================================================================
	// Getters

	public get pan()
	{
		return this._pan;
	}

	public get pinchZoom()
	{
		return this._pinchZoom;
	}

	public get pointerDetector()
	{
		return this._pointerDetector;
	}
}